// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Send a Contact-page message. Phase 1 posts to a local server stub that
// validates + logs and returns { status: 'ok' }; Phase 2 forwards to tph-api
// (persist + Postmark). Plain JSON (not Transit) — server uses express.json(),
// so we fetch directly with JSON.stringify (the shared `post` helper only
// serializes Transit bodies). Mirrors src/util/searchTelemetry.js.
//
// See `server/api/contact.js`.
export const sendContactMessage = async body => {
  const response = await window.fetch(`${apiBaseUrl()}/api/contact`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`Contact proxy responded ${response.status}`);
  }
  return response.json();
};

// Story 4.2.2 — website "Contact Seller" inquiry door. POSTs the inquiry-modal
// submission to the marketplace's own /api/inquiries proxy (which resolves the
// buyer server-side and forwards to tph-api). Plain JSON, and the response is
// NOT thrown on non-2xx: the response-state matrix (two distinct 200s, two
// distinct 409s, 422, 5xx) is data the duck thunk branches on, so this returns
// { ok, status, body, retryAfter } like the verify wrappers in util/onboarding.
// Rejects only on transport failure (network error / no fetch).
//
// See `server/api/inquiries.js`.
export const postWebsiteInquiry = async body => {
  const response = await window.fetch(`${apiBaseUrl()}/api/inquiries`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  let parsed;
  try {
    parsed = await response.json();
  } catch (e) {
    parsed = {};
  }
  return {
    ok: response.ok,
    status: response.status,
    body: parsed,
    retryAfter: response.headers.get('Retry-After'),
  };
};

// Story 3.9 (AC4) — account-setup convergence notify, fired right after
// email/password signup succeeds. The server proxy resolves the user uuid
// from the session and forwards to tph-api with the internal token, where any
// held slot grants + checkout T&C attach. AWAITED by the signup thunk
// (bounded server-side) so the onboarding wizard already sees just-attached
// credits — but NEVER throws: a failed notify must not fail signup.
//
// Story 3.10 correction: the old comment claimed "the signup-completion
// re-trigger self-heals". It did not — that tph-api route had no callers and
// has been deleted. The real self-healing lane is tph-api's `*/5`
// heldGrantSweep cron. Plain JSON, empty body.
//
// See `server/api/account-setup-notify.js`.
// Story 3.11 (AC1.4): an optional ClickFunnels order id rides this call when
// one was captured. Absent ⇒ the posted body is `{}`, exactly as before.
// Story 3.19 (AC6): `source` says WHICH population this call came from, so
// tph-api's `account_setup_processed` line can tell an authenticated arrival
// apart from a signup — the database cannot, because both stamp
// attach_source='cf_order' and there is no `source` column (ledger #777).
// It DEFAULTS to 'marketplace_signup', so the two existing signup callers are
// byte-identical and neither had to change.
export const notifyAccountSetup = async (cfOrder, source) => {
  try {
    const body = cfOrder ? { cfOrder } : {};
    if (source) {
      body.source = source;
    }
    const response = await window.fetch(`${apiBaseUrl()}/api/account-setup-notify`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return await response.json().catch(() => ({ ok: false }));
  } catch (e) {
    return { ok: false, reason: 'transport_error' };
  }
};

// Story 3.15 — the will-call claim page's two calls.
//
// Both go through the same-origin `server/api/` proxies, never straight to
// tph-api: the internal token is injected server-side and must never reach the
// browser.
//
// ⚠️ The token rides the URL PATH. Do not log these URLs, and do not put the
// token in a query param — a path segment is what tph-api's logRedaction
// scrubs, and a query param would additionally end up in Referer headers.

// Read-only probe. NEVER consumes the token — mail gateways prefetch links, and
// a consuming GET would burn a paying customer's only recovery route before she
// saw it. Returns { outcome, tier?, slotCount? }.
export const probeSlotClaim = async token => {
  try {
    const response = await window.fetch(
      `${apiBaseUrl()}/api/slot-claim/${encodeURIComponent(token)}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    return await response.json().catch(() => ({ outcome: 'unavailable' }));
  } catch (e) {
    return { outcome: 'unavailable', reason: 'transport_error' };
  }
};

// Redeem. Requires an authenticated session — the relay resolves the uuid from
// it and refuses to take one from the body.
//
// ⚠️ Returns `{ ok }` and NOTHING ELSE, by design (review B-2(a)). The counters
// stay server-side; handing them back would turn this into a verification
// oracle over a credential space. The page re-probes to decide what to render.
export const redeemSlotClaim = async token => {
  try {
    const response = await window.fetch(
      `${apiBaseUrl()}/api/slot-claim/${encodeURIComponent(token)}/redeem`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      }
    );
    return await response.json().catch(() => ({ ok: false }));
  } catch (e) {
    return { ok: false, reason: 'transport_error' };
  }
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

// Create a Stripe billing-portal session for the signed-in user and resolve
// with { url }. The server proxy (and the tph-api endpoint behind it) ship in
// a follow-up story — until then this rejects and BillingSubscriptionsPage
// shows its inline error.
export const createBillingPortalSession = () => {
  return post('/api/billing/portal-session', {});
};

// Story 3.16 (AC5.4) — ask-outcome telemetry. Same-origin POST; the server
// proxy forwards the CUSTOMER token and tph-api stamps `user_uuid` from it.
// ⚠️ The body must NEVER carry an identity — see util/askOutcome.js and RP-9.
export const recordCheckoutHandoffEvent = body => {
  return post('/api/checkout-handoff-events', body);
};

// Story 7.19 (AC3, Task 10) — the retro-link call. Fire-and-forget: NEVER
// awaited by the caller, every error swallowed here so a failed link can never
// fail sign-in/boot/hydration (9.18's "a stale session cookie must not disable
// the whole site" behaviour stays green). The server resolves `user_uuid` from
// the session itself (never trusts a body value); `visitor_session_id` is the
// one thing only the browser knows (D1 — localStorage, not a server-readable
// cookie like 16-4's `tph_vid`), so it rides the body raw.
//
// See `server/api/visitor-link.js`.
export const linkVisitor = visitorSessionId => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function' || !visitorSessionId) {
    return Promise.resolve(null);
  }
  // Ticket 88 Part 1 (D8): resolves to the proxy's relayed answer from tph-api,
  // `{ linked, reason }`, or null when there is none (a bodiless 204, a
  // non-2xx, a transport failure). NEVER rejects — a failed link must never
  // affect sign-in or boot. `src/util/visitorLink.js` rotates the visitor id
  // when `reason` is 'visitor_already_linked'.
  return window
    .fetch('/api/visitor-link', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitor_session_id: visitorSessionId }),
    })
    .then(response => {
      if (!response || response.status !== 200 || typeof response.json !== 'function') {
        return null;
      }
      return response
        .json()
        .then(
          body =>
            body && typeof body === 'object'
              ? { linked: body.linked === true, reason: body.reason || null }
              : null,
          () => null
        );
    })
    .catch(() => null);
};

// Story 4.6 — the /c/:token confirmation page's three calls.
//
// Plain JSON against the same-origin proxy (server/api/inquiryContact.js),
// which injects the internal token and forwards to tph-api. NO credentials
// are sent: the reader is not a marketplace user and has no session. The
// token in the path is the entire credential — never log it.
//
// These resolve on 4xx rather than throwing, because the OUTCOME is data the
// page renders (expired / unavailable / confirmed / declined), not an error
// state. They reject only on transport failure.
const inquiryContactRequest = async (path, options) => {
  const response = await window.fetch(`${apiBaseUrl()}${path}`, options);
  if (response.status >= 500) {
    throw new Error(`Inquiry-contact proxy responded ${response.status}`);
  }
  return response.json();
};

export const fetchInquiryContactInvite = token =>
  inquiryContactRequest(`/api/inquiry-contact/${encodeURIComponent(token)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

export const confirmInquiryContact = (token, email) =>
  inquiryContactRequest(`/api/inquiry-contact/${encodeURIComponent(token)}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(email === undefined ? {} : { email }),
  });

export const declineInquiryContact = token =>
  inquiryContactRequest(`/api/inquiry-contact/${encodeURIComponent(token)}/decline`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });

// Story 4.6 — the wizard card's four OWNER-facing calls, against the same-origin
// proxy (server/api-util/tphInquiryContactProxy.js), which forwards the signed-in
// owner's own Sharetribe token to tph-api. `credentials: 'include'` is required:
// the proxy reads the access token from the session cookie store.
//
// Like `postWebsiteInquiry` above, these do NOT throw on non-2xx — the status
// matrix IS data the card branches on (409 contact_declined_final, 429
// rate_limited with retry_after_seconds, 404 no_pending_contact). They resolve
// { ok, status, body } and reject only on transport failure.
const listingInquiryContactRequest = async (path, options) => {
  const response = await window.fetch(`${apiBaseUrl()}${path}`, {
    credentials: 'include',
    ...options,
  });
  let parsed;
  try {
    parsed = await response.json();
  } catch (e) {
    parsed = {};
  }
  return { ok: response.ok, status: response.status, body: parsed };
};

const inquiryContactPath = listingId =>
  `/api/listings/${encodeURIComponent(listingId)}/inquiry-contact`;

export const fetchListingInquiryContact = listingId =>
  listingInquiryContactRequest(inquiryContactPath(listingId), {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

// `body` is either { full_name, phone } (a new contact) or { inquiry_contact_id }
// (reuse of a saved one) — tph-api's schema accepts exactly one of the two.
export const saveListingInquiryContact = (listingId, body) =>
  listingInquiryContactRequest(inquiryContactPath(listingId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

export const resendListingInquiryContact = listingId =>
  listingInquiryContactRequest(`${inquiryContactPath(listingId)}/resend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });

export const deleteListingInquiryContact = listingId =>
  listingInquiryContactRequest(inquiryContactPath(listingId), {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
  });

// ---------------------------------------------------------------------------
// Step 6 / story 15.11 — the LIVE ad chrome.
//
// Same-origin against `server/api-util/tphAdPreviewProxy.js`, which forwards
// the signed-in owner's own Sharetribe access token to tph-api's
// `requireCustomerToken`-guarded `GET /api/v1/listings/:uuid/ad-preview`.
// `credentials: 'include'` is required — the proxy reads the access token from
// the session cookie store, exactly as the inquiry-contact calls above do.
//
// 🔴 THE STATUS IS DATA, NOT AN ERROR. Like `listingInquiryContactRequest`,
// this does NOT throw on non-2xx: the wizard branches on the typed upstream
// code (409 `ad_preview_blocked_banned_word` carries `details.banned`, the
// only thing that lets the page tell the seller which field to fix). It
// rejects only on transport failure.
//
// 🔴 `cache: 'no-store'` IS DELIBERATE, AND IT IS NOT A CACHE REFUSAL.
// The caller holds the ETag itself and sends `If-None-Match` explicitly (see
// `adChromeSeam.js`), so revalidation is exact and observable. Leaving the
// browser's own HTTP cache in the loop as well would add a second, invisible
// revalidation layer that can serve a synthesized 200 from a body we already
// hold — costing a duplicate blob for the same bytes and making the 304 path
// untestable. One cache, in one place.
export const fetchAdChromeSheet = async (listingUuid, { etag, signal } = {}) => {
  const headers = { Accept: 'image/png' };
  if (etag) {
    headers['If-None-Match'] = etag;
  }
  const response = await window.fetch(
    `${apiBaseUrl()}/api/listings/${encodeURIComponent(listingUuid)}/ad-preview`,
    { method: 'GET', credentials: 'include', cache: 'no-store', headers, signal }
  );

  if (response.status === 304) {
    return { status: 304, etag: response.headers.get('etag') || etag, blob: null, body: null };
  }
  if (response.ok) {
    return {
      status: response.status,
      etag: response.headers.get('etag') || null,
      blob: await response.blob(),
      body: null,
    };
  }

  let body = null;
  try {
    body = await response.json();
  } catch (e) {
    body = null;
  }
  return { status: response.status, etag: null, blob: null, body };
};
